/**
 * Zod schemas for:
 *   - `Observation`: the in-memory shape the grammar evaluator hands up
 *      for one parsed `@observe` entry. NOT persisted — lives inside CRDT
 *      while the body is live, then freezes into the body markdown.
 *   - `ArtefactObservationBody`: the `artefact_observation_bodies` DDL row.
 *
 * Mirrors `../types/observation.ts`. Spec authority: `architecture.md §4.7`
 * + `cook-as-process-design.md §6.5`.
 *
 * Per the implementer brief, `ObservationSchema` is modelled as a
 * discriminated union on `owner` so the runtime discriminator can narrow
 * cleanly even though all three owner variants share one field shape today.
 */

import { z } from "zod";
import type {
  ArtefactObservationBody,
  Observation,
  ObservationField,
  ObservationOwner,
} from "../types/observation.js";
import { UUID_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const timestamp = z.number().int().nonnegative();

export const ObservationOwnerSchema: z.ZodType<ObservationOwner> = z.enum([
  "artefact",
  "attempt",
  "batch",
]);

export const ObservationFieldSchema = z
  .object({
    label: z.string(),
    raw: z.string(),
  })
  .strict();

const observationBase = {
  ownerUuid: uuid,
  at: timestamp,
  fields: z.array(ObservationFieldSchema),
};

const ArtefactObservationVariantSchema = z
  .object({ owner: z.literal("artefact"), ...observationBase })
  .strict();

const AttemptObservationVariantSchema = z
  .object({ owner: z.literal("attempt"), ...observationBase })
  .strict();

const BatchObservationVariantSchema = z
  .object({ owner: z.literal("batch"), ...observationBase })
  .strict();

export const ObservationSchema = z.discriminatedUnion("owner", [
  ArtefactObservationVariantSchema,
  AttemptObservationVariantSchema,
  BatchObservationVariantSchema,
]);

export const ArtefactObservationBodySchema = z
  .object({
    artefactUuid: uuid,
    bodyMarkdown: z.string(),
    bodyDocUuid: uuid,
    updatedAt: timestamp,
  })
  .strict();

type _AssertField =
  z.infer<typeof ObservationFieldSchema> extends ObservationField
    ? ObservationField extends z.infer<typeof ObservationFieldSchema>
      ? true
      : never
    : never;
type _AssertObservation =
  z.infer<typeof ObservationSchema> extends Observation
    ? Observation extends z.infer<typeof ObservationSchema>
      ? true
      : never
    : never;
type _AssertArtefactObservationBody =
  z.infer<typeof ArtefactObservationBodySchema> extends ArtefactObservationBody
    ? ArtefactObservationBody extends z.infer<typeof ArtefactObservationBodySchema>
      ? true
      : never
    : never;
export type __ObservationSchemaAsserts = [
  _AssertField,
  _AssertObservation,
  _AssertArtefactObservationBody,
];
